import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ErrorState, Field, FormError, Input, LoadingState, Select, Textarea } from '@lawbee/ui';
import { useState, type FormEvent } from 'react';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { PROVINCES, TIMEZONES, useApiMutation } from '../../lib/hooks';
import { can, ME_KEY, useMe } from '../../lib/me';
import type { SettingsResponse } from '../../lib/types';
import { CopyField, ReadOnlyNote } from '../app/ops/shared';
import { SettingsCard } from './layout';

export const SETTINGS_KEY = ['workspace-settings'] as const;

export function useWorkspaceSettings() {
  return useQuery({ queryKey: SETTINGS_KEY, queryFn: () => api.get<SettingsResponse>('/api/workspace/settings') });
}

export function FirmSettingsPage() {
  const q = useWorkspaceSettings();
  if (q.isLoading) return <LoadingState label="Loading firm settings" />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <div className="space-y-6">
      <FirmForm data={q.data} />
      <PublicLinks data={q.data} />
    </div>
  );
}

function FirmForm({ data }: { data: SettingsResponse }) {
  const me = useMe();
  const qc = useQueryClient();
  const canEdit = can(me, 'settings');
  const [name, setName] = useState(data.workspace.name);
  const [timezone, setTimezone] = useState(data.workspace.timezone);
  const [region, setRegion] = useState(data.workspace.region ?? '');
  const [phone, setPhone] = useState(data.settings.phone ?? '');
  const [address, setAddress] = useState(data.settings.address ?? '');
  const [replyTo, setReplyTo] = useState(data.settings.replyToEmail ?? '');
  const [notice, setNotice] = useState(data.settings.confidentialityNotice ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const tzOptions = TIMEZONES.some((t) => t.value === timezone) ? TIMEZONES : [{ value: timezone, label: timezone }, ...TIMEZONES];

  const save = useApiMutation((body: Record<string, unknown>) => api.patch<SettingsResponse>('/api/workspace/settings', body), {
    success: 'Firm details saved',
    invalidate: [SETTINGS_KEY],
    onSuccess: () => void qc.invalidateQueries({ queryKey: ME_KEY }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (name.trim().length < 2) next.name = 'Enter your firm name.';
    if (replyTo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyTo)) next.replyToEmail = 'Enter a valid email address.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setFormError(null);
    save.mutate(
      {
        name: name.trim(),
        timezone,
        region: region || null,
        settings: { phone: phone.trim() || null, address: address.trim() || null, replyToEmail: replyTo.trim() || null, confidentialityNotice: notice },
      },
      {
        onError: (err) => {
          setErrors(fieldErrors(err));
          setFormError(errorMessage(err));
        },
      },
    );
  };

  return (
    <form onSubmit={submit} noValidate>
      <SettingsCard
        title="Firm details"
        description="Shown on your intake forms, booking pages, invoices and client emails."
        footer={
          canEdit ? (
            <Button type="submit" loading={save.isPending}>
              Save changes
            </Button>
          ) : undefined
        }
      >
        <fieldset disabled={!canEdit} className="space-y-5">
          {!canEdit ? <ReadOnlyNote /> : null}
          <Field label="Firm name" htmlFor="fs-name" required error={errors.name}>
            <Input id="fs-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="organization" aria-invalid={!!errors.name || undefined} />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Time zone" htmlFor="fs-tz">
              <Select id="fs-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)} options={tzOptions} />
            </Field>
            <Field label="Province or territory" htmlFor="fs-region">
              <Select id="fs-region" value={region} onChange={(e) => setRegion(e.target.value)} options={PROVINCES} placeholder="Select" />
            </Field>
            <Field label="Phone" htmlFor="fs-phone">
              <Input id="fs-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            </Field>
            <Field label="Reply-to email" htmlFor="fs-reply" error={errors.replyToEmail ?? errors['settings.replyToEmail']} description="Client replies to LawBee emails go here.">
              <Input id="fs-reply" type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} autoComplete="email" />
            </Field>
          </div>
          <Field label="Office address" htmlFor="fs-address">
            <Textarea id="fs-address" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} autoComplete="street-address" />
          </Field>
          <Field
            label="Confidentiality notice"
            htmlFor="fs-notice"
            description="Shown at the top of your intake forms and booking pages, before a client shares anything."
            error={errors['settings.confidentialityNotice']}
          >
            <Textarea id="fs-notice" rows={4} maxLength={2000} value={notice} onChange={(e) => setNotice(e.target.value)} />
          </Field>
          <FormError message={formError} />
        </fieldset>
      </SettingsCard>
    </form>
  );
}

function PublicLinks({ data }: { data: SettingsResponse }) {
  const u = data.publicUrls;
  return (
    <SettingsCard title="Public links" description="Share these on your website, in email signatures and with new clients.">
      <div className="space-y-4">
        {u.firmPage ? <CopyField label="Firm page" value={u.firmPage} description="Lists your intake forms and booking options in one place." /> : null}
        <CopyField label="Intake forms" value={u.intakeBase} description="Each intake form has its own address under this one." />
        <CopyField label="Booking" value={u.bookingBase} description="Each booking type has its own address under this one." />
        <CopyField label="Client portal" value={u.portal} description="Clients sign in with a magic link sent to their email." />
      </div>
    </SettingsCard>
  );
}
